/**
 * Словари интерфейса.
 *
 * Разложены по разделам, а не одним файлом: полторы тысячи ключей в одном
 * JSON нечитаемы, а слияние правок двух человек в нём — гарантированный
 * конфликт. Имя файла становится верхним уровнем ключа: `map.json` даёт
 * `map.layers.solo` (§11, фаза 10, задача 2), и раздел интерфейса ищется
 * в словаре так же, как в дереве исходников.
 *
 * Импорты перечислены руками, а не собраны `import.meta.glob`: словарь
 * читают не только приложение и его проверки, но и `e2e` под Playwright,
 * где `import.meta.glob` не существует. Забыть здесь файл нельзя молча —
 * `scripts/check-i18n.mjs` сверяет этот список с содержимым каталогов
 * и роняет сборку (задача 8).
 */

import ruAgents from './locales/ru/agents.json';
import ruArchive from './locales/ru/archive.json';
import ruAudit from './locales/ru/audit.json';
import ruAuth from './locales/ru/auth.json';
import ruCommon from './locales/ru/common.json';
import ruCredentials from './locales/ru/credentials.json';
import ruErrors from './locales/ru/errors.json';
import ruEvents from './locales/ru/events.json';
import ruFiles from './locales/ru/files.json';
import ruHealth from './locales/ru/health.json';
import ruHelp from './locales/ru/help.json';
import ruMap from './locales/ru/map.json';
import ruMonitors from './locales/ru/monitors.json';
import ruNav from './locales/ru/nav.json';
import ruNodes from './locales/ru/nodes.json';
import ruNotifications from './locales/ru/notifications.json';
import ruOrganization from './locales/ru/organization.json';
import ruOverview from './locales/ru/overview.json';
import ruSessions from './locales/ru/sessions.json';
import ruSites from './locales/ru/sites.json';
import ruSsh from './locales/ru/ssh.json';
import ruUsers from './locales/ru/users.json';
import ruValidation from './locales/ru/validation.json';
import ruVideo from './locales/ru/video.json';
import ruVnc from './locales/ru/vnc.json';

import enAgents from './locales/en/agents.json';
import enArchive from './locales/en/archive.json';
import enAudit from './locales/en/audit.json';
import enAuth from './locales/en/auth.json';
import enCommon from './locales/en/common.json';
import enCredentials from './locales/en/credentials.json';
import enErrors from './locales/en/errors.json';
import enEvents from './locales/en/events.json';
import enFiles from './locales/en/files.json';
import enHealth from './locales/en/health.json';
import enHelp from './locales/en/help.json';
import enMap from './locales/en/map.json';
import enMonitors from './locales/en/monitors.json';
import enNav from './locales/en/nav.json';
import enNodes from './locales/en/nodes.json';
import enNotifications from './locales/en/notifications.json';
import enOrganization from './locales/en/organization.json';
import enOverview from './locales/en/overview.json';
import enSessions from './locales/en/sessions.json';
import enSites from './locales/en/sites.json';
import enSsh from './locales/en/ssh.json';
import enUsers from './locales/en/users.json';
import enValidation from './locales/en/validation.json';
import enVideo from './locales/en/video.json';
import enVnc from './locales/en/vnc.json';

import type { Language } from './language';

/** Разделы словаря. Имя раздела — первый сегмент ключа. */
export const AREAS = [
  'agents',
  'archive',
  'audit',
  'auth',
  'common',
  'credentials',
  'errors',
  'events',
  'files',
  'health',
  'help',
  'map',
  'monitors',
  'nav',
  'nodes',
  'notifications',
  'organization',
  'overview',
  'sessions',
  'sites',
  'ssh',
  'users',
  'validation',
  'video',
  'vnc',
] as const;

const ru = {
  agents: ruAgents,
  archive: ruArchive,
  audit: ruAudit,
  auth: ruAuth,
  common: ruCommon,
  credentials: ruCredentials,
  errors: ruErrors,
  events: ruEvents,
  files: ruFiles,
  health: ruHealth,
  help: ruHelp,
  map: ruMap,
  monitors: ruMonitors,
  nav: ruNav,
  nodes: ruNodes,
  notifications: ruNotifications,
  organization: ruOrganization,
  overview: ruOverview,
  sessions: ruSessions,
  sites: ruSites,
  ssh: ruSsh,
  users: ruUsers,
  validation: ruValidation,
  video: ruVideo,
  vnc: ruVnc,
};

const en = {
  agents: enAgents,
  archive: enArchive,
  audit: enAudit,
  auth: enAuth,
  common: enCommon,
  credentials: enCredentials,
  errors: enErrors,
  events: enEvents,
  files: enFiles,
  health: enHealth,
  help: enHelp,
  map: enMap,
  monitors: enMonitors,
  nav: enNav,
  nodes: enNodes,
  notifications: enNotifications,
  organization: enOrganization,
  overview: enOverview,
  sessions: enSessions,
  sites: enSites,
  ssh: enSsh,
  users: enUsers,
  validation: enValidation,
  video: enVideo,
  vnc: enVnc,
};

/**
 * Ресурсы в том виде, в каком их ждёт i18next.
 *
 * Пространство имён одно (`translation`): разделять их незачем — словарь
 * попадает в бандл целиком, отложенной загрузки нет, а один общий корень
 * позволяет ссылаться из карты на общую кнопку без указания пространства.
 */
export const resources: Record<Language, { translation: Record<string, unknown> }> = {
  ru: { translation: ru },
  en: { translation: en },
};
